import {
  BeaconFilterFieldTypeSchema,
  WeekReferencesSchema,
  LogicalOperatorsSchema,
  ComparisonOperatorsSchema,
  OperatorTypeSchema,
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/schema/AlertBuilderSchema';
import {
  RuleGroupConfigType,
  RuleBlockConfigType,
  OperatorConfigType,
  TimeRangeModesType,
  FilterConfigType,
  TimeConfigurationType,
  WeekReferencesType,
  WeekReferenceOperatorType,
  BeaconFilterFieldTypeType,
  OperatorType,
  EmailNotificationConfigType,
  WebhookSecurityMethodType,
  WebhookNotificationConfigType,
  LogicalOperatorsType,
  CreateAlertConfigType,
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/schema/AlertBuilderSchemaTypes';
import { AlertTemplateIdType, getTemplateConfigByPlatform } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/schema/template';
import { AppName } from 'sl-api-connector';

/**
 * Gets the largest (most recent) week ID from available week IDs
 * @param allWeekIds - Array of available week IDs (required, guaranteed to not be empty)
 * @returns The largest week ID
 */
export const getLargestWeekId = (allWeekIds: string[]): number => {
  // Convert to numbers, sort descending, and return the largest
  const sortedWeekIds = allWeekIds
    .map(id => parseInt(id, 10))
    .filter(id => !isNaN(id))
    .sort((a, b) => b - a);

  // Since allWeekIds is guaranteed to have valid data, this should always work
  return sortedWeekIds[0];
};

export const DEFAULT_TIME_RANGE = 'Last Week';
export const DEFAULT_COMPARISON_OPTION = 'Prior Year';
export const DEFAULT_FOR_THE_FIRST_TIME_OPTION = 'Last Week';

// Default logical operator
export const DEFAULT_LOGICAL_OPERATOR = 'AND';

// Default values for relative time configuration (typed to literal unions)
export const DEFAULT_RELATIVE_REFERENCE: WeekReferencesType = 'last_week';
export const DEFAULT_RELATIVE_OPERATOR: WeekReferenceOperatorType = '+';
export const DEFAULT_RELATIVE_OFFSET = 0;

export const DEFAULT_RELATIVE_BEFORE_WEEK_TIME_CONFIGURATION: TimeConfigurationType = {
  mode: 'Before Week',
  beforeWeek: {
    useRelativeBeforeWeek: true,
    relativeBeforeWeekReference: DEFAULT_RELATIVE_REFERENCE,
    relativeBeforeWeekOperator: DEFAULT_RELATIVE_OPERATOR,
    relativeBeforeWeekOffset: DEFAULT_RELATIVE_OFFSET
  }
};

export const DEFAULT_RELATIVE_AFTER_WEEK_TIME_CONFIGURATION: TimeConfigurationType = {
  mode: 'After Week',
  afterWeek: {
    useRelativeAfterWeek: true,
    relativeAfterWeekReference: DEFAULT_RELATIVE_REFERENCE,
    relativeAfterWeekOperator: DEFAULT_RELATIVE_OPERATOR,
    relativeAfterWeekOffset: DEFAULT_RELATIVE_OFFSET
  }
};

/**
 * Creates a default rule block configuration
 * @param allWeekIds - Array of available week IDs for better defaults
 */
export const createDefaultRuleBlock = (allWeekIds?: string[]): RuleBlockConfigType => ({
  metric: 'Total Traffic',
  aggregation: 'sum',
  timeConfiguration: createTimeConfiguration('Predefined Period', allWeekIds),
  categoricalfilters: []
});

/**
 * Creates a default rule group configuration
 * @param allWeekIds - Array of available week IDs for better defaults
 */
export const createDefaultRuleGroup = (allWeekIds: string[]): RuleGroupConfigType => ({
  ruleBlocks: [createDefaultRuleBlock(allWeekIds)],
  operators: []
});

/**
 * Creates a default operator between rule blocks
 */
export const createDefaultRuleBlockOperator = (
  operatorType: OperatorType = DEFAULT_LOGICAL_OPERATOR
): OperatorConfigType => ({
  type: operatorType
});

/**
 * Creates time configuration based on mode
 * @param mode - The time configuration mode
 * @param allWeekIds - Array of available week IDs to determine the best default
 * @param weekId - Optional specific week ID to use instead of largest available
 */
export const createTimeConfiguration = (
  mode: TimeRangeModesType,
  allWeekIds?: string[],
  weekId?: number
): TimeConfigurationType => {
  // Use provided weekId, or largest available week ID, or fallback to default
  const actualWeekId = weekId ?? getLargestWeekId(allWeekIds);
  switch (mode) {
    case 'Predefined Period':
      return {
        mode: mode as 'Predefined Period',
        timeRange: DEFAULT_TIME_RANGE
      };

    case 'Predefined Comparison':
      return {
        mode: mode as 'Predefined Comparison',
        timeRange: DEFAULT_TIME_RANGE,
        comparisonOption: DEFAULT_COMPARISON_OPTION
      };

    case 'Before Week':
      return {
        mode: mode as 'Before Week',
        beforeWeek: {
          useRelativeBeforeWeek: false as const,
          beforeWeekValue: actualWeekId
        }
      };

    case 'After Week':
      return {
        mode: mode as 'After Week',
        afterWeek: {
          useRelativeAfterWeek: false as const,
          afterWeekValue: actualWeekId
        }
      };

    case 'Custom Range':
      return {
        mode: mode as 'Custom Range',
        customRange: {
          useRelativeStartWeek: false as const,
          startWeek: actualWeekId,
          useRelativeEndWeek: false as const,
          endWeek: actualWeekId
        }
      };

    case 'For the First Time':
      return {
        mode: mode as 'For the First Time',
        forTheFirstTimeOption: DEFAULT_FOR_THE_FIRST_TIME_OPTION
      };

    default:
      // Fallback to Predefined Period
      return {
        mode: 'Predefined Period' as const,
        timeRange: DEFAULT_TIME_RANGE
      };
  }
};

/**
 * Creates time configuration for a specific week value
 */
export const createWeekTimeConfiguration = (mode: 'Before Week' | 'After Week', weekValue: number) => {
  if (mode === 'Before Week') {
    return {
      mode: mode as 'Before Week',
      beforeWeek: {
        useRelativeBeforeWeek: false as const,
        beforeWeekValue: weekValue
      }
    };
  } else {
    return {
      mode: mode as 'After Week',
      afterWeek: {
        useRelativeAfterWeek: false as const,
        afterWeekValue: weekValue
      }
    };
  }
};

/**
 * Creates predefined period time configuration
 */
export const createPredefinedPeriodTimeConfiguration = (timeRange: string) => ({
  mode: 'Predefined Period' as const,
  timeRange
});

/**
 * Creates predefined comparison time configuration
 */
export const createPredefinedComparisonTimeConfiguration = (timeRange: string, comparisonOption: string) => ({
  mode: 'Predefined Comparison' as const,
  timeRange,
  comparisonOption
});

/**
 * Creates for the first time time configuration
 */
export const createForTheFirstTimeTimeConfiguration = (forTheFirstTimeOption: string) => ({
  mode: 'For the First Time' as const,
  forTheFirstTimeOption
});

/**
 * Creates custom range time configuration
 */
export const createCustomRangeTimeConfiguration = (startWeek: number, endWeek: number) => ({
  mode: 'Custom Range' as const,
  customRange: {
    useRelativeStartWeek: false as const,
    startWeek,
    useRelativeEndWeek: false as const,
    endWeek
  }
});

// Removed deprecated CATEGORICAL_FILTER_TYPES. Use schema enum options instead.

/**
 * Creates a default categorical filter configuration
 */
export const createDefaultCategoricalFilter = (): FilterConfigType => ({
  operator: 'IN',
  values: []
});

/**
 * Segments and Business Units are singleton types.
 * We don't allow adding more than one of these types to a rule block.
 * Also, if there is already a non-singleton type, we don't allow adding a singleton type to a rule block.
 * This will disable the "Add Categorical Filter" button for the rule block, if there is already a singleton type.
 */
export const SINGLETON_CATEGORICAL_TYPES: BeaconFilterFieldTypeType[] = ['Segment', 'Business Unit'];

/**
 * Returns the list of non-singleton categorical types based on schema options.
 */
export const getNonSingletonCategoricalTypes = (): BeaconFilterFieldTypeType[] =>
  BeaconFilterFieldTypeSchema.options.filter(
    (t) => !SINGLETON_CATEGORICAL_TYPES.includes(t as BeaconFilterFieldTypeType)
  ) as BeaconFilterFieldTypeType[];

/**
 * Determines if the "+ Add Categorical Filter" button should be disabled
 * given current filters, based on singleton presence and max non-singleton count.
 */
export const isAddCategoricalDisabled = (filters: Array<{ type?: string }>): boolean => {
  const hasSingleton = filters.some((f) =>
    SINGLETON_CATEGORICAL_TYPES.includes(f.type as BeaconFilterFieldTypeType)
  );
  if (hasSingleton) return true;
  return filters.length >= getNonSingletonCategoricalTypes().length;
};

/**
 * Generic helper: disable add when the number of rows reaches the size of the allowed type pool.
 * Useful for Reference Metric filters or any de-duped type rows.
 */
export const isAddDisabledByTypePool = (
  filters: Array<{ type?: string }>,
  allowedTypes: ReadonlyArray<string>
): boolean => filters.length >= allowedTypes.length;

// Avoid the selected option chips to overflow for more than 1 line
export const LETTERS_LIMIT = 40;
export const MAX_PILLS = 4;

export const UI_WEEK_REFERENCES = [
  { id: WeekReferencesSchema.enum['beginning'], display: 'Beginning of Data' },
  { id: WeekReferencesSchema.enum['last_week'], display: 'Last Week' },
  { id: WeekReferencesSchema.enum['year_start'], display: 'Start of Year' },
  { id: WeekReferencesSchema.enum['last_52w'], display: '52 Weeks Ago' },
  { id: WeekReferencesSchema.enum['last_26w'], display: '26 Weeks Ago' },
  { id: WeekReferencesSchema.enum['last_13w'], display: '13 Weeks Ago' },
  { id: WeekReferencesSchema.enum['last_4w'], display: '4 Weeks Ago' },
];

export const createDefaultWebhookConfig = (url: string = '', security: WebhookSecurityMethodType = 'urlKey'): WebhookNotificationConfigType => {
  if (security === 'bearerToken') {
    return {
      url,
      security,
      bearerToken: ''
    };
  }

  return {
    url,
    security
  };
};

export const DROPDOWN_WIDTH = '313px';

/**
 * Operator constraint utilities for rule groups
 */

/**
 * Determines the dominant logical operator type in a list of operators
 * @param operators - Array of operators to analyze
 * @returns The dominant logical operator or null if none/mixed
 */
export const getDominantLogicalOperator = (operators: OperatorConfigType[]): OperatorType | null => {
  const logicalOperators = operators
    .map(op => op.type)
    .filter((type): type is OperatorType => LogicalOperatorsSchema.options.includes(type as any));

  if (logicalOperators.length === 0) return null;

  const hasAnd = logicalOperators.includes(LogicalOperatorsSchema.enum['AND']);
  const hasOr = logicalOperators.includes(LogicalOperatorsSchema.enum['OR']);

  if (hasAnd && !hasOr) return LogicalOperatorsSchema.enum['AND'];
  if (hasOr && !hasAnd) return LogicalOperatorsSchema.enum['OR'];
  return null;
};

/**
 * Gets available operator options based on previous operators in the same group
 * @param previousOperators - Array of operators that come before the current position
 * @param allOperatorOptions - All available operator options from schema
 * @returns Filtered array of available operator types
 */
export const getConstrainedOperatorOptions = (
  previousOperators: OperatorConfigType[],
  allOperatorOptions: readonly OperatorType[] = OperatorTypeSchema.options
): OperatorType[] => {
  // Early return: if no previous operators, all options available
  if (previousOperators.length === 0) {
    return [...allOperatorOptions];
  }

  const dominantOperator = getDominantLogicalOperator(previousOperators);

  if (!dominantOperator) {
    return [...allOperatorOptions];
  }

  // Return constrained options: dominant logical operator + comparison operators
  return [dominantOperator, ...ComparisonOperatorsSchema.options];
};

/**
 * Validates if an operator choice is valid given the constraints
 * @param operatorType - The operator type to validate
 * @param previousOperators - Array of operators that come before the current position
 * @returns true if the operator choice is valid
 */
export const isOperatorChoiceValid = (
  operatorType: OperatorType,
  previousOperators: OperatorConfigType[]
): boolean => {
  const availableOptions = getConstrainedOperatorOptions(previousOperators);
  return availableOptions.includes(operatorType);
};

/**
 * Cascades logical operator changes to subsequent operators in the group for consistency
 * @param operators - All operators in the group
 * @param changedOperatorIndex - Index of the operator that was changed
 * @param newLogicalOperator - The new logical operator ('AND' or 'OR')
 * @returns Updated operators array with cascaded changes
 */
export const cascadeLogicalOperatorChange = (
  operators: OperatorConfigType[],
  changedOperatorIndex: number,
  newLogicalOperator: LogicalOperatorsType
): OperatorConfigType[] => {
  // Early return if not a logical operator or no operators to cascade to
  if (!LogicalOperatorsSchema.options.includes(newLogicalOperator) ||
    changedOperatorIndex >= operators.length - 1) {
    return operators;
  }

  const updatedOperators = [...operators];

  // Find all subsequent operators that need to be updated
  for (let i = changedOperatorIndex + 1; i < operators.length; i++) {
    const operator = operators[i];

    // Only cascade to logical operators, leave comparison operators unchanged
    if (LogicalOperatorsSchema.options.includes(operator.type as any)) {
      // Only update if it's different from the new logical operator
      if (operator.type !== newLogicalOperator) {
        updatedOperators[i] = {
          ...operator,
          type: newLogicalOperator,
        };
      }
    }
  }

  return updatedOperators;
};

/**
 * Checks if a logical operator change should trigger cascading updates
 * @param oldOperator - The previous operator type
 * @param newOperator - The new operator type
 * @returns true if cascading is needed
 */
export const shouldCascadeLogicalOperator = (
  oldOperator: OperatorType | undefined,
  newOperator: OperatorType
): boolean => {
  // Only cascade if both old and new are logical operators and they're different
  const isOldLogical = oldOperator && LogicalOperatorsSchema.options.includes(oldOperator as any);
  const isNewLogical = LogicalOperatorsSchema.options.includes(newOperator as any);

  return isOldLogical && isNewLogical && oldOperator !== newOperator;
};

/**
 * Gets operator constraints for a given position within rule groups
 * @param operators - All operators in the current rule group
 * @param currentOperatorIndex - Index of the current operator being edited
 * @returns Object with constrained options and helper functions
 */
/**
 * Utility function to generate human-readable time period descriptions
 * @param timeConfiguration - The time configuration object
 * @returns A human-readable description of the time period
 */
export const generateTimePeriodDescription = (timeConfiguration: TimeConfigurationType): string => {
  // Handle null/undefined cases
  if (!timeConfiguration || !timeConfiguration.mode) {
    return 'No time period configured';
  }

  switch (timeConfiguration.mode) {
    case 'Predefined Period':
      return timeConfiguration.timeRange || 'Predefined Period';

    case 'Predefined Comparison':
      const timeRange = timeConfiguration.timeRange || 'Unknown Period';
      const comparisonOption = timeConfiguration.comparisonOption || 'Unknown Comparison';
      return `${timeRange} - ${comparisonOption}`;

    case 'For the First Time':
      const forTheFirstTimeOption = timeConfiguration.forTheFirstTimeOption || 'Last Week';
      return `For the First Time - ${forTheFirstTimeOption}`;

    case 'Custom Range':
      const { customRange } = timeConfiguration;
      if (!customRange) {
        return 'Custom Range (not configured)';
      }

      let startDesc = '';
      let endDesc = '';

      // Generate start description
      if (customRange.useRelativeStartWeek) {
        const operator = customRange.relativeStartOperator || '+';
        const reference = customRange.relativeStartReference?.replace(/_/g, ' ') || 'unknown reference';
        const offset = customRange.relativeStartOffset ?? 0;
        startDesc = `(${reference} ${operator} ${offset} weeks)`;
      } else {
        startDesc = `Week ${customRange.startWeek ?? 'unknown'}`;
      }

      // Generate end description
      if (customRange.useRelativeEndWeek) {
        const operator = customRange.relativeEndOperator || '+';
        const reference = customRange.relativeEndReference?.replace(/_/g, ' ') || 'unknown reference';
        const offset = customRange.relativeEndOffset ?? 0;
        endDesc = `(${reference} ${operator} ${offset} weeks)`;
      } else {
        endDesc = `Week ${customRange.endWeek ?? 'unknown'}`;
      }

      return `${startDesc} to ${endDesc}`;

    case 'Before Week':
      const { beforeWeek } = timeConfiguration;
      if (!beforeWeek) {
        return 'Before Week (not configured)';
      }

      if (beforeWeek.useRelativeBeforeWeek === true) {
        const operator = beforeWeek.relativeBeforeWeekOperator || '+';
        const reference = beforeWeek.relativeBeforeWeekReference?.replace(/_/g, ' ') || 'unknown reference';
        const offset = beforeWeek.relativeBeforeWeekOffset ?? 0;
        return `Before (${reference} ${operator} ${offset} weeks)`;
      } else {
        return `Before Week ${beforeWeek.beforeWeekValue ?? 'unknown'}`;
      }

    case 'After Week':
      const { afterWeek } = timeConfiguration;
      if (!afterWeek) {
        return 'After Week (not configured)';
      }

      if (afterWeek.useRelativeAfterWeek === true) {
        const operator = afterWeek.relativeAfterWeekOperator || '+';
        const reference = afterWeek.relativeAfterWeekReference?.replace(/_/g, ' ') || 'unknown reference';
        const offset = afterWeek.relativeAfterWeekOffset ?? 0;
        return `After (${reference} ${operator} ${offset} weeks)`;
      } else {
        return `After Week ${afterWeek.afterWeekValue ?? 'unknown'}`;
      }

    default:
      return 'Custom time range';
  }
};

export const getOperatorConstraints = (
  operators: OperatorConfigType[],
  currentOperatorIndex: number
) => {
  // Calculate previous operators
  const previousOperators = operators.slice(0, currentOperatorIndex);

  // Get available options
  const availableOptions = getConstrainedOperatorOptions(previousOperators, OperatorTypeSchema.options);

  // Get dominant operator
  const dominantLogicalOperator = getDominantLogicalOperator(previousOperators);

  // Calculate constraint info
  const hasConstraints = previousOperators.length > 0 && dominantLogicalOperator !== null;
  const isConstrained = availableOptions.length < OperatorTypeSchema.options.length;

  const constraintInfo = {
    hasConstraints,
    isConstrained,
    dominantOperator: dominantLogicalOperator,
    constraintReason: hasConstraints
      ? `Only ${dominantLogicalOperator} and comparison operators allowed in this group`
      : 'No constraints - first operator in group'
  };

  // Cascading handler
  const getCascadedOperators = (
    currentOperator: OperatorConfigType | undefined,
    newOperatorType: OperatorType,
    updatedOperator: OperatorConfigType
  ): OperatorConfigType[] => {
    let updatedOperators = [...operators];
    updatedOperators[currentOperatorIndex] = updatedOperator;

    // Check if we need to cascade the logical operator change
    if (shouldCascadeLogicalOperator(currentOperator?.type, newOperatorType)) {
      updatedOperators = cascadeLogicalOperatorChange(
        updatedOperators,
        currentOperatorIndex,
        newOperatorType as LogicalOperatorsType
      );
    }

    return updatedOperators;
  };

  return {
    availableOptions,
    constraintInfo,
    isValidChoice: (operatorType: OperatorType) => availableOptions.includes(operatorType),
    getNextOperatorForNewRule: () => dominantLogicalOperator || LogicalOperatorsSchema.enum['AND'],
    getCascadedOperators
  };
};

/**
 * Alert mode type
 */
export type AlertMode = 'Create' | 'Edit';

/**
 * Determines the alert mode based on URL path
 * @param pathname - Current URL pathname
 * @returns The determined alert mode
 */
export const determineAlertMode = (pathname: string): AlertMode => {
  // If we're on the edit path, it's Edit mode
  if (pathname === AlertsTabsPath.editAlert) {
    return 'Edit';
  }

  // Otherwise, it's Create mode (including templates)
  return 'Create';
};

/**
 * Extracts the alertId from URL search parameters
 * @param searchParams - URL search parameters string
 * @returns The alertId if found, null otherwise
 */
export const extractAlertIdFromSearchParams = (searchParams: string): string | null => {
  const urlParams = new URLSearchParams(searchParams);
  const alertId = urlParams.get('alertId');

  if (!alertId) {
    return null;
  }

  let decodedAlertId = decodeURIComponent(alertId);

  // Clean up alertId if it contains extra search parameters (fallback safety)
  if (decodedAlertId.includes('?')) {
    decodedAlertId = decodedAlertId.split('?')[0];
  }

  return decodedAlertId;
};

export enum AlertsTab {
  MyAlerts = 'myAlerts',
  TeamAlerts = 'teamAlerts'
}

export enum AlertsTabsDisplayName {
  myAlerts = 'My Alerts',
  teamAlerts = 'Team Alerts'
}

export enum AlertsTabsPath {
  myAlerts = '/account/alerts',
  teamAlerts = '/account/alerts/team',
  createAlert = '/account/alerts/create',
  templateAlert = '/account/alerts/template',
  editAlert = '/account/alerts/edit'
}

/**
 * Helper function to generate edit alert path with alertId as search param
 * @param alertId - The alert ID to edit
 * @returns The complete edit path with the encoded alertId as a query parameter
 */
export const getEditAlertPath = (alertId: string): string => {
  // URI encode the alertId to handle special characters
  const encodedAlertId = encodeURIComponent(alertId);
  return `${AlertsTabsPath.editAlert}?alertId=${encodedAlertId}`;
};
